import { useCallback, useEffect, useMemo, useState } from 'react';
import { type CoaCardProps } from '../components/CoaCard';
import { getPublicCoaResults } from './useLandingPages';
import { normalizePublicLandingUrl } from '../landingPage/publicLandingUrl';

export const COA_RESULTS_ERROR_MESSAGE = 'COA results could not be loaded.';
export const COA_RESULTS_REQUEST_TIMEOUT_MS = 30_000;

type PackageCoaResponse = {
  id?: number | string;
  packageId?: number | string;
  created?: string;
  data?: string | Record<string, any>;
  coaFiles?: string[];
  coaFileNames?: string[];
  coaFileEntries?: {
    testPerformedDate?: string;
    labTestingStateName?: string;
    labFacilityName?: string;
    labFacilityLicenseNumber?: string;
  }[];
};

export type PublicCoaResults = {
  coas: CoaCardProps[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
};

export type PublicCoaFile = {
  url: string;
  testerName: string;
  testPerformedDate: string;
};

export type PublicCoaFiles = {
  files: PublicCoaFile[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
};

type RequestState<T> = {
  key: string;
  results: T[];
  isLoading: boolean;
  error: string | null;
};

const value = (input: unknown): string => typeof input === 'string' ? input.trim() : '';

const object = (input: unknown): Record<string, unknown> => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('Invalid COA results record.');
  }
  return input as Record<string, unknown>;
};

const documentDate = (input: unknown): string => {
  const raw = value(input);
  const calendar = raw.match(/^\d{4}-\d{2}-\d{2}(?=T|$)/)?.[0];
  if (!calendar || !Number.isFinite(Date.parse(raw))) return '';
  const parsed = new Date(`${calendar}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === calendar ? calendar : '';
};

const attachmentIdentity = (input: string): string => {
  const url = new URL(input);
  url.hash = '';
  // Azure SAS credentials rotate without changing the document; other query fields identify it.
  if (url.searchParams.has('sig') && url.searchParams.has('sv')) {
    for (const key of [
      'sig', 'sv', 'st', 'se', 'sp', 'spr', 'sr', 'sip', 'si', 'ss', 'srt',
      'skoid', 'sktid', 'skt', 'ske', 'sks', 'skv',
    ]) url.searchParams.delete(key);
  }
  url.searchParams.sort();
  return url.href;
};

const sortedCoaFiles = (files: Map<string, PublicCoaFile>): PublicCoaFile[] => [...files.entries()]
  .sort(([leftKey, left], [rightKey, right]) => (
    right.testPerformedDate.localeCompare(left.testPerformedDate) || leftKey.localeCompare(rightKey)
  ))
  .map(([, file]) => file);

const selectCoaFiles = (
  selectedUrls: string[],
  metadata: PublicCoaFile[],
  preserveSelectedOrder: boolean,
): PublicCoaFile[] => {
  const byIdentity = new Map(metadata.map(file => [attachmentIdentity(file.url), file]));
  const files = new Map<string, PublicCoaFile>();
  for (const candidate of selectedUrls) {
    const url = normalizePublicLandingUrl(candidate);
    if (!url) continue;
    const identity = attachmentIdentity(url);
    if (files.has(identity)) continue;
    const match = byIdentity.get(identity);
    files.set(identity, {
      url,
      testerName: match?.testerName || '',
      testPerformedDate: match?.testPerformedDate || '',
    });
  }
  // Active landing payloads already carry the package's saved display order.
  return preserveSelectedOrder ? [...files.values()] : sortedCoaFiles(files);
};

export const toPublicCoaFiles = (records: unknown): PublicCoaFile[] => {
  if (!Array.isArray(records)) throw new Error('Invalid COA results response.');
  const files = new Map<string, PublicCoaFile>();
  for (const candidate of records) {
    const record = object(candidate);
    if (record.coaFiles == null) continue;
    if (!Array.isArray(record.coaFiles)) throw new Error('Invalid COA attachment list.');
    if (record.coaFileEntries != null && !Array.isArray(record.coaFileEntries)) {
      throw new Error('Invalid COA attachment metadata.');
    }
    record.coaFiles.forEach((candidateUrl, index) => {
      const url = normalizePublicLandingUrl(candidateUrl);
      if (!url) return;
      const entry = Array.isArray(record.coaFileEntries) && record.coaFileEntries[index] != null
        ? object(record.coaFileEntries[index]) : {};
      const identity = attachmentIdentity(url);
      const existing = files.get(identity);
      files.set(identity, {
        url: existing?.url ?? url,
        testerName: existing?.testerName || value(entry.labFacilityName),
        testPerformedDate: existing?.testPerformedDate || documentDate(entry.testPerformedDate),
      });
    });
  }
  return sortedCoaFiles(files);
};

// Public responses deliberately omit database snapshot IDs. Canonicalize the public content
// so tied timestamps cannot change selection when response order or signed URL queries change.
const canonicalSnapshotValue = (input: unknown): unknown => {
  if (Array.isArray(input)) return input.map(canonicalSnapshotValue);
  if (input && typeof input === 'object') {
    const object = input as Record<string, unknown>;
    return Object.fromEntries(Object.keys(object).sort().map(key => [key, canonicalSnapshotValue(object[key])]));
  }
  return input;
};

const dateOnly = (input: string): string => input.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || input;

const latestFileTestDate = (entries: PackageCoaResponse['coaFileEntries']): string => {
  if (!Array.isArray(entries)) return '';
  const dates = entries
    .map((entry) => dateOnly(value(entry?.testPerformedDate)))
    .filter(Boolean)
    .sort();
  return dates.length ? dates[dates.length - 1] : '';
};

export const sortCoasByTestDate = (coas: CoaCardProps[]): CoaCardProps[] => [...coas].sort((left, right) => (
  right.date.localeCompare(left.date)
));

export function toCoaCard(record: PackageCoaResponse): CoaCardProps | null {
  try {
    // The API returns `data` as a JSON string or as an already-structured object.
    const data = (typeof record.data === 'string'
      ? JSON.parse(record.data || '{}')
      : record.data ?? {}) as Record<string, any>;
    const id = value(data.id) || String(record.id || '');
    if (!id) return null;

    return {
      id,
      cultivatorName: value(data.manufacturer?.name),
      testerName: value(data.lab?.name),
      labLicenseNumber: value(data.lab?.licenseNumber),
      date: dateOnly(
        latestFileTestDate(record.coaFileEntries)
        || value(data.dateTested)
        || value(data.testedDate)
        || value(record.created),
      ),
      labStatus: value(data.labTestingStateName),
      files: Array.isArray(record.coaFiles)
        ? record.coaFiles
          .map((url, index) => ({
            url: value(url),
            fileName: Array.isArray(record.coaFileNames) ? value(record.coaFileNames[index]) : '',
            testPerformedDate: Array.isArray(record.coaFileEntries)
              ? dateOnly(value(record.coaFileEntries[index]?.testPerformedDate))
              : '',
            labTestingStateName: Array.isArray(record.coaFileEntries)
              ? value(record.coaFileEntries[index]?.labTestingStateName)
              : '',
            testerName: Array.isArray(record.coaFileEntries)
              ? value(record.coaFileEntries[index]?.labFacilityName)
              : '',
            labLicenseNumber: Array.isArray(record.coaFileEntries)
              ? value(record.coaFileEntries[index]?.labFacilityLicenseNumber)
              : '',
            sourceIndex: index,
          }))
          .filter((file) => file.url)
          .sort((left, right) => (
            right.testPerformedDate.localeCompare(left.testPerformedDate)
            || right.sourceIndex - left.sourceIndex
          ))
          .map(({ sourceIndex: _sourceIndex, ...file }) => file)
        : [],
    };
  } catch {
    return null;
  }
}

export const toCoaCards = (records: unknown): CoaCardProps[] => {
  const occurrences = new Map<string, number>();
  const coas = (Array.isArray(records) ? records : []).flatMap((record) => {
    const coa = toCoaCard(record);
    if (!coa) return [];
    const data = typeof record?.data === 'string' ? JSON.parse(record.data || '{}') : record?.data ?? {};
    const identity = JSON.stringify([
      coa.id, value(record?.created), canonicalSnapshotValue(data),
      coa.files.map(({ url, ...file }) => ({ ...file, url: url.split(/[?#]/)[0] })),
    ]);
    // Only publicly indistinguishable duplicates need an occurrence suffix; their displayed
    // content and attachment locations are identical, so reordering cannot select different data.
    const occurrence = occurrences.get(identity) || 0;
    occurrences.set(identity, occurrence + 1);
    return [{ ...coa, selectionKey: JSON.stringify([identity, occurrence]) }];
  });
  return sortCoasByTestDate(coas);
};

/**
 * Owns COA request sequencing, cancellation, mapping, error state and retry so the route container
 * only maps URL parameters to presentation.
 */
function useMappedPublicCoaResults<T>(
  issuanceId: string, landingIndex: number, map: (records: unknown) => T[],
) {
  const [attempt, setAttempt] = useState(0);
  const requestKey = JSON.stringify([issuanceId, landingIndex, attempt]);
  const [state, setState] = useState<RequestState<T>>({
    key: requestKey,
    results: [],
    isLoading: Boolean(issuanceId),
    error: null,
  });

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    if (!issuanceId) {
      return;
    }

    setState({ key: requestKey, results: [], isLoading: true, error: null });

    let cancelled = false;
    const controller = new AbortController();
    const aborted = new Promise<never>((_, reject) => {
      controller.signal.addEventListener('abort', () => reject(controller.signal.reason), { once: true });
    });
    const timeoutId = window.setTimeout(() => {
      controller.abort(new Error('COA results request timed out after 30 seconds'));
    }, COA_RESULTS_REQUEST_TIMEOUT_MS);

    Promise.race([getPublicCoaResults(issuanceId, landingIndex, controller.signal), aborted])
      .then((records) => {
        if (cancelled) return;
        const results = map(records);
        if (!cancelled) setState((current) => current.key === requestKey
          ? { ...current, results } : current);
      })
      .catch(() => {
        // A failed request is not an empty package: surface it so the page can offer a retry.
        if (!cancelled) setState((current) => current.key === requestKey
          ? { ...current, error: COA_RESULTS_ERROR_MESSAGE } : current);
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (!cancelled) setState((current) => current.key === requestKey
          ? { ...current, isLoading: false } : current);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort(new Error('COA results request cancelled'));
    };
  }, [issuanceId, landingIndex, requestKey, map]);

  // Effects run after render. Hide a previous issuance during the first render under a new URL.
  if (state.key !== requestKey) {
    return { results: [], isLoading: Boolean(issuanceId), error: null, retry };
  }

  return { results: state.results, isLoading: state.isLoading, error: state.error, retry };
}

export function usePublicCoaResults(issuanceId: string, landingIndex: number): PublicCoaResults {
  const { results: coas, ...state } = useMappedPublicCoaResults(issuanceId, landingIndex, toCoaCards);
  return { coas, ...state };
}

export function usePublicCoaFiles(
  issuanceId: string,
  landingIndex: number,
  selectedUrls: string[],
  preserveSelectedOrder = false,
): PublicCoaFiles {
  const { results: metadata, ...state } = useMappedPublicCoaResults(issuanceId, landingIndex, toPublicCoaFiles);
  const files = useMemo(
    () => selectCoaFiles(selectedUrls, metadata, preserveSelectedOrder),
    [selectedUrls, metadata, preserveSelectedOrder],
  );
  return { files, ...state };
}
